/* ==========================================================================
   concierge-agent.js — AI Shopping Concierge with in-chat add-to-cart.
   - Multi-turn conversation
   - Parses garment / occasion / colour / budget from natural English
   - Recommends fabrics from real Shopify inventory
   - Renders product cards inline in chat bubble with Add to Cart button
   - Demo mode (default): rule-based dialogue.
     LIVE mode: set window.themeSettings.aiBackendUrl + '/api/concierge'
   ========================================================================== */
(function () {
  'use strict';

  const messagesEl = document.querySelector('[data-chat-messages]');
  if (!messagesEl) return;
  const form = document.querySelector('[data-chat-form]');
  const input = document.querySelector('[data-chat-input]');
  const typingEl = document.querySelector('[data-chat-typing]');

  // --------- Conversation state ----------
  const state = {
    // Groups this visitor's messages together in the "AI Chat Log" sheet, so a whole
    // conversation's token use and cost can be totalled. Not a customer identifier —
    // it's random per chat and holds nothing personal.
    sessionId: 'S' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
    phone: null,   // collected before the chat starts — see the phone gate below
    history: [],   // [{role:'user'|'assistant', content}] — conversation memory for the AI
    garment: null,
    occasion: null,
    colour: null,
    metres: null,
    budget: null,
    persons: null,
    season: null,
    location: null,
    showedProducts: false,
    turn: 0
  };

  // --------- Sales-focused scenarios ----------
  // Each scenario has: opener (validates choice), styling_tip (helps visualise),
  // metres_min / metres_max (with tailor buffer), label (for fabric plan card),
  // tags (ordered list of fabric types to search).
  // The opener and styling tip vary by garment+occasion for personality.
  const SCENARIOS = {
    'saree:wedding': {
      opener: 'a heirloom-grade Kanchipuram or Banaras silk saree is a once-in-a-lifetime choice ✨',
      styling_tip: 'Go for jewel tones — crimson, peacock blue, emerald, or temple gold. The borders carry the story.',
      buyer_hook: 'Bridal silks at the studio start under ₹3,000/m — we hand-pick every piece. Several gold-zari options arrived this week.',
      metres_min: 6.0, metres_max: 6.5, buffer: 0.5, label: 'BRIDAL SAREE',
      tags: ['silk', 'kanchipuram', 'banaras', 'brocade']
    },
    'saree:festival': {
      opener: 'a soft pure-silk saree is the festive favourite at the studio 🪔',
      styling_tip: 'Mid-tone colours — mustard, deep teal, oxblood, ivory — photograph beautifully and pair with simple jewellery.',
      buyer_hook: 'Most festive silks at RIDH sit between ₹1,500–₹2,800/m, with embroidered options for that little something extra.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'FESTIVE SAREE',
      tags: ['silk', 'chanderi', 'organza']
    },
    'saree:party': {
      opener: 'an organza or soft-silk saree drapes effortlessly for evening looks 🥂',
      styling_tip: 'Pastels (champagne, blush, sage) for cocktail evenings; deeper hues (wine, midnight, plum) for night events.',
      buyer_hook: 'Organza sarees start at ₹1,200/m and are flying out — we got a new shipment in this week.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'PARTY SAREE',
      tags: ['organza', 'silk', 'georgette']
    },
    'saree:formal': {
      opener: 'a fine handloom cotton saree is the elegant office choice 🪞',
      styling_tip: 'Stick to muted solids — slate, off-white, ash-grey, deep mustard. Pair with a contrast blouse.',
      buyer_hook: 'Handloom cotton sarees at RIDH are ₹450–₹950/m — the kind that gets better with every wash.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'OFFICE SAREE',
      tags: ['cotton', 'linen']
    },
    'saree:casual': {
      opener: 'a breezy mul-cotton or linen saree is perfect for everyday elegance 🌿',
      styling_tip: 'Hand-block prints, indigo dyes, and earthy beiges work beautifully for daily wear.',
      buyer_hook: 'Cotton handlooms at the studio start at ₹350/m — small-batch, looms in Kanchipuram and Salem.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'DAILY SAREE',
      tags: ['cotton', 'linen']
    },

    'kurta:wedding': {
      opener: 'a silk or brocade kurta with structure carries the look beautifully 🌟',
      styling_tip: 'Ivory and gold are the classics; deep teal, wine, and forest green look incredible in photographs.',
      buyer_hook: 'Brocade kurta fabrics at RIDH start at ₹1,800/m — most pieces have woven gold-zari motifs.',
      metres_min: 2.5, metres_max: 3.0, buffer: 0.5, label: 'WEDDING KURTA',
      tags: ['silk', 'brocade']
    },
    'kurta:festival': {
      opener: 'a hand-loom cotton or soft-silk kurta is the festive classic 🪔',
      styling_tip: 'Earth tones — mustard, terracotta, deep sage — feel warm and traditional. Add a stole or chunky beads.',
      buyer_hook: 'Festive cotton & silk kurta fabrics at RIDH start at ₹650/m.',
      metres_min: 2.5, metres_max: 3.0, buffer: 0.5, label: 'FESTIVE KURTA',
      tags: ['silk', 'cotton', 'chanderi']
    },
    'kurta:party': {
      opener: 'a sharp silk-blend kurta is exactly the right call for evenings 🥂',
      styling_tip: 'Try monochrome (ink-black, charcoal, deep navy) or jewel tones with a subtle sheen.',
      buyer_hook: 'Party kurta fabrics at RIDH start at ₹900/m and include some lovely textured silks.',
      metres_min: 2.5, metres_max: 3.0, buffer: 0.5, label: 'PARTY KURTA',
      tags: ['silk', 'linen']
    },
    'kurta:formal': {
      opener: 'a linen or cotton kurta in a clean cut is your office workhorse 🪞',
      styling_tip: 'Pale beige, slate, off-white, and dusty olive — they layer beautifully under a Nehru jacket.',
      buyer_hook: 'Linen and cotton kurta fabrics at RIDH are ₹450–₹800/m. Easy to maintain, gets softer with wear.',
      metres_min: 2.5, metres_max: 3.0, buffer: 0.5, label: 'OFFICE KURTA',
      tags: ['linen', 'cotton']
    },
    'kurta:casual': {
      opener: 'an everyday cotton kurta is the most-loved garment in any wardrobe 🌿',
      styling_tip: 'Hand-block prints, indigo, or solid pastels — versatile, breathable, easy on the iron.',
      buyer_hook: 'Cotton kurta fabrics at RIDH start at ₹350/m — small-batch, weavers we trust.',
      metres_min: 2.5, metres_max: 3.0, buffer: 0.5, label: 'DAILY KURTA',
      tags: ['cotton', 'linen']
    },

    'shirt:wedding': {
      opener: 'a crisp linen shirt is the easy-elegant choice for weddings 🌟',
      styling_tip: 'Off-white, deep navy, or sage green for receptions; ivory or pale grey for daytime functions.',
      buyer_hook: 'Premium linen at RIDH is ₹750–₹1,200/m — Belgian and Indian linens, both 60-Lea grade.',
      metres_min: 1.75, metres_max: 2.25, buffer: 0.25, label: 'WEDDING SHIRT',
      tags: ['linen', 'cotton']
    },
    'shirt:festival': {
      opener: 'a hand-loom cotton shirt feels just right for festivals — light, breathable, and traditional 🪔',
      styling_tip: 'Earth tones (mustard, terracotta, indigo) or whites with subtle prints work beautifully.',
      buyer_hook: 'Festive cotton at RIDH starts at ₹450/m — hand-block prints arriving weekly.',
      metres_min: 1.75, metres_max: 2.25, buffer: 0.25, label: 'FESTIVE SHIRT',
      tags: ['cotton', 'linen']
    },
    'shirt:party': {
      opener: 'a crisp linen shirt is the secret to dressing well for parties — sharp without trying too hard 🥂',
      styling_tip: 'Pastels (sage, dusty pink, butter) for daytime parties; deeper tones (wine, ink, charcoal) for evening.',
      buyer_hook: 'Linen shirts at RIDH are ₹750–₹1,200/m. Most pieces hold a crease for hours — perfect for long evenings.',
      metres_min: 1.75, metres_max: 2.25, buffer: 0.25, label: 'PARTY SHIRT',
      tags: ['linen', 'cotton']
    },
    'shirt:formal': {
      opener: 'a sharp linen-cotton shirt is the Chennai-summer workhorse 🪞',
      styling_tip: 'Stick to office-friendly solids — white, light blue, pale grey, soft beige.',
      buyer_hook: 'Linen-cotton shirting at RIDH is ₹600–₹950/m — wrinkle-resistant, machine-washable.',
      metres_min: 1.75, metres_max: 2.25, buffer: 0.25, label: 'OFFICE SHIRT',
      tags: ['linen', 'cotton']
    },
    'shirt:casual': {
      opener: 'a soft cotton or mul-cotton shirt is ideal for staying cool and stylish this summer ☀️',
      styling_tip: 'Go for light, earthy tones — beige, ivory, sage green, dusty pink. They reflect heat and look effortlessly sharp in Chennai summers.',
      buyer_hook: 'Mul-cotton and breezy cotton shirts at RIDH start at ₹350/m — most are pre-shrunk.',
      metres_min: 1.75, metres_max: 2.25, buffer: 0.25, label: 'CASUAL SHIRT',
      tags: ['cotton', 'linen']
    },

    'lehenga:wedding': {
      opener: 'a silk + brocade lehenga is the bridal classic ✨',
      styling_tip: 'Red, maroon, and gold for traditional weddings; pink, peach, or pastel sage for modern ones.',
      buyer_hook: 'Bridal lehenga fabrics at RIDH start at ₹2,000/m for silk, ₹2,800/m for brocade — we pre-test for embroidery hold.',
      metres_min: 7.5, metres_max: 8.5, buffer: 0.5, label: 'BRIDAL LEHENGA',
      tags: ['silk', 'brocade', 'velvet']
    },
    'lehenga:festival': {
      opener: 'a silk lehenga in a festive jewel tone is always a crowd-favourite 🪔',
      styling_tip: 'Emerald, teal, mustard, and deep maroon — colours that hold up beautifully under festive lights.',
      buyer_hook: 'Festive lehenga silks at RIDH start at ₹1,500/m, brocades at ₹2,200/m.',
      metres_min: 6.5, metres_max: 7.5, buffer: 0.5, label: 'FESTIVE LEHENGA',
      tags: ['silk', 'brocade']
    },
    'lehenga:party': {
      opener: 'an organza or velvet lehenga gives that evening drama 🥂',
      styling_tip: 'Black, wine, midnight blue, or deep emerald — colours that own the room.',
      buyer_hook: 'Party lehenga fabrics at RIDH start at ₹1,200/m for organza, ₹1,800/m for velvet.',
      metres_min: 6.5, metres_max: 7.5, buffer: 0.5, label: 'PARTY LEHENGA',
      tags: ['organza', 'silk', 'velvet']
    },

    'blouse:wedding': {
      opener: 'a brocade or silk blouse adds the perfect contrast to a bridal saree ✨',
      styling_tip: 'Brocade in gold-zari is timeless; pure silk in a contrast colour is the modern bride favourite.',
      buyer_hook: 'Blouse pieces at RIDH start at ₹800/m for silk, ₹1,800/m for brocade.',
      metres_min: 0.9, metres_max: 1.1, buffer: 0.1, label: 'BRIDAL BLOUSE',
      tags: ['silk', 'brocade']
    },
    'blouse:festival': {
      opener: 'a silk or brocade blouse pairs beautifully with festive sarees 🪔',
      styling_tip: 'Match the saree border or contrast with a complementary jewel tone.',
      buyer_hook: 'Festive blouse fabric at RIDH starts at ₹750/m.',
      metres_min: 0.9, metres_max: 1.1, buffer: 0.1, label: 'FESTIVE BLOUSE',
      tags: ['silk', 'brocade']
    },

    'sherwani:wedding': {
      opener: 'a silk or brocade sherwani brings traditional grandeur to the look ✨',
      styling_tip: 'Ivory, gold, beige, and deep maroon are the classic palette. Add embroidery sparingly.',
      buyer_hook: 'Sherwani fabrics at RIDH start at ₹1,800/m for silk, ₹2,800/m for brocade. We can recommend tailors in Egmore.',
      metres_min: 3.5, metres_max: 4.0, buffer: 0.5, label: 'WEDDING SHERWANI',
      tags: ['silk', 'brocade', 'velvet']
    },
    'sherwani:festival': {
      opener: 'a brocade sherwani carries festive weight beautifully 🪔',
      styling_tip: 'Deep maroon, forest green, or royal blue — they look stunning under warm lighting.',
      buyer_hook: 'Festive sherwani fabrics at RIDH start at ₹1,500/m.',
      metres_min: 3.5, metres_max: 4.0, buffer: 0.5, label: 'FESTIVE SHERWANI',
      tags: ['silk', 'brocade']
    },

    'dupatta:wedding': {
      opener: 'a delicate organza or chanderi dupatta tops the bridal look ✨',
      styling_tip: 'Match the lehenga, or go contrast — a sheer dupatta over a heavy lehenga looks stunning.',
      buyer_hook: 'Bridal dupattas at RIDH start at ₹1,200/m.',
      metres_min: 2.25, metres_max: 2.75, buffer: 0.25, label: 'BRIDAL DUPATTA',
      tags: ['silk', 'organza', 'chanderi']
    },
    'dupatta:festival': {
      opener: 'a chanderi or silk dupatta is the festive must-have 🪔',
      styling_tip: 'Light gold or ivory for daytime; deeper colours for evening events.',
      buyer_hook: 'Festive dupattas at RIDH start at ₹650/m.',
      metres_min: 2.25, metres_max: 2.75, buffer: 0.25, label: 'FESTIVE DUPATTA',
      tags: ['silk', 'chanderi']
    },

    'anarkali:wedding': {
      opener: 'a silk or organza anarkali flows beautifully for celebrations ✨',
      styling_tip: 'Ivory, gold, blush, sage, or wine — colours that flatter on the dance floor.',
      buyer_hook: 'Anarkali fabrics at RIDH start at ₹1,200/m.',
      metres_min: 4.75, metres_max: 5.25, buffer: 0.25, label: 'WEDDING ANARKALI',
      tags: ['silk', 'brocade', 'organza']
    },
    'anarkali:party': {
      opener: 'an organza anarkali gives that effortless evening flow 🥂',
      styling_tip: 'Pastels for daytime parties; deep jewel tones for evenings.',
      buyer_hook: 'Organza anarkali fabrics at RIDH start at ₹1,000/m.',
      metres_min: 4.75, metres_max: 5.25, buffer: 0.25, label: 'PARTY ANARKALI',
      tags: ['organza', 'silk']
    },

    'gown:wedding': {
      opener: 'a silk or brocade gown reads as modern bridal elegance ✨',
      styling_tip: 'Champagne, blush, deep wine, or midnight — sophisticated palette.',
      buyer_hook: 'Wedding gown fabrics at RIDH start at ₹1,800/m.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'WEDDING GOWN',
      tags: ['silk', 'brocade']
    },
    'gown:festival': {
      opener: 'a silk-organza or chanderi gown is the festive showstopper 🪔',
      styling_tip: 'Jewel tones — emerald, sapphire, ruby — look incredible under festive lights. Pair with traditional jewellery.',
      buyer_hook: 'Festive gown fabrics at RIDH start at ₹1,500/m. Most pieces drape effortlessly without heavy lining.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'FESTIVE DRESS',
      tags: ['silk', 'organza', 'chanderi']
    },
    'gown:party': {
      opener: 'a silk-organza or velvet gown owns the evening 🥂',
      styling_tip: 'Black is bulletproof; emerald, wine, and royal blue are showstoppers.',
      buyer_hook: 'Party gown fabrics at RIDH start at ₹1,500/m.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'PARTY GOWN',
      tags: ['silk', 'organza', 'velvet']
    },
    'gown:formal': {
      opener: 'a linen or chambray gown reads as quiet sophistication 🪞',
      styling_tip: 'Solid pastels or muted neutrals layer beautifully at work.',
      buyer_hook: 'Linen and chambray gown fabrics at RIDH are ₹600–₹1,200/m.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'OFFICE GOWN',
      tags: ['linen', 'cotton']
    },
    'gown:casual': {
      opener: 'a soft cotton or rayon gown is effortless everyday elegance 🌿',
      styling_tip: 'Pastels, breezy florals, or solid earth tones for ease.',
      buyer_hook: 'Cotton and rayon gown fabrics at RIDH start at ₹450/m.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'EVERYDAY DRESS',
      tags: ['cotton', 'linen']
    },
    'gown:any': {
      opener: 'a versatile silk or cotton gown works across occasions ✨',
      styling_tip: 'For most occasions — soft silks lift the look, cottons keep it comfortable.',
      buyer_hook: 'Gown fabrics at RIDH start at ₹450/m.',
      metres_min: 5.75, metres_max: 6.25, buffer: 0.25, label: 'GOWN / DRESS',
      tags: ['silk', 'cotton']
    }
  };

  // --------- Garment-occasion → fabric guidance rule book ----------
  // Principle: GARMENT decides the fabric family. OCCASION refines within the family.
  // tags = ordered list; we try them in order until search returns results.
  const RULES = {
    // SAREE — silk base, vary by occasion
    'saree:wedding':    { tags: ['silk', 'kanchipuram', 'banaras', 'brocade'], metres: 6, note: 'Kanchipuram and Banaras silks are timeless for bridal sarees.' },
    'saree:festival':   { tags: ['silk', 'chanderi', 'organza'],               metres: 6, note: 'Pure silk sarees lend grace to any festive look.' },
    'saree:party':      { tags: ['organza', 'silk', 'georgette'],              metres: 6, note: 'Organza or soft silk works beautifully for evening occasions.' },
    'saree:formal':     { tags: ['cotton', 'linen'],                            metres: 6, note: 'Cotton and linen sarees keep their structure for the office.' },
    'saree:casual':     { tags: ['cotton', 'linen'],                            metres: 6, note: 'Cotton and linen breathe well — perfect for daily elegance.' },
    'saree:any':        { tags: ['silk', 'cotton'],                             metres: 6, note: 'For a saree, silk or fine cotton always works.' },

    // KURTA — varies more by occasion
    'kurta:wedding':    { tags: ['silk', 'brocade'],                            metres: 2.5, note: 'Silk or brocade kurtas hold structure for celebrations.' },
    'kurta:festival':   { tags: ['silk', 'cotton', 'chanderi'],                 metres: 2.5, note: 'A festive kurta in silk or hand-loom cotton always looks the part.' },
    'kurta:party':      { tags: ['silk', 'linen'],                              metres: 2.5, note: 'For a party kurta — silk feels rich, linen feels effortless.' },
    'kurta:formal':     { tags: ['linen', 'cotton'],                            metres: 2.5, note: 'Linen-cotton kurtas give a sharp finish for work.' },
    'kurta:casual':     { tags: ['cotton', 'linen'],                            metres: 2.5, note: 'Cotton and linen kurtas are easy to wear and easy to love.' },
    'kurta:any':        { tags: ['cotton', 'silk', 'linen'],                    metres: 2.5, note: 'Most kurtas are stitched in cotton or silk.' },

    // LEHENGA
    'lehenga:wedding':  { tags: ['silk', 'brocade', 'velvet'],                  metres: 8, note: 'For a lehenga, allow 8 metres including the dupatta. Silk and brocade hold heavy work best.' },
    'lehenga:festival': { tags: ['silk', 'brocade'],                            metres: 7, note: 'Festive lehenga — silk or brocade with about 7 metres.' },
    'lehenga:party':    { tags: ['organza', 'silk', 'velvet'],                  metres: 7, note: 'Organza or velvet gives that evening drama.' },
    'lehenga:any':      { tags: ['silk', 'brocade'],                            metres: 8, note: 'Lehengas need silk or brocade for that hold and shape.' },

    // BLOUSE
    'blouse:wedding':   { tags: ['silk', 'brocade'],                            metres: 1, note: 'A blouse needs 1 metre. Brocade contrasts beautifully against a plain saree.' },
    'blouse:festival':  { tags: ['silk', 'brocade'],                            metres: 1, note: '1 metre of silk or brocade does the job.' },
    'blouse:any':       { tags: ['silk', 'cotton'],                             metres: 1, note: 'Just 1 metre — silk or fine cotton for the blouse.' },

    // SHERWANI
    'sherwani:wedding': { tags: ['silk', 'brocade', 'velvet'],                  metres: 3.5, note: 'Sherwanis need 3.5 metres. Silk or brocade for structure, velvet for winter weddings.' },
    'sherwani:festival':{ tags: ['silk', 'brocade'],                            metres: 3.5, note: 'Brocade sherwanis carry festive weight beautifully.' },
    'sherwani:any':     { tags: ['silk', 'brocade'],                            metres: 3.5, note: 'A sherwani always begins with silk or brocade.' },

    // ANARKALI
    'anarkali:wedding': { tags: ['silk', 'brocade', 'organza'],                 metres: 5, note: 'Anarkali needs around 5 metres for that flow.' },
    'anarkali:party':   { tags: ['organza', 'silk'],                            metres: 5, note: 'Organza flares beautifully — try 5 metres.' },
    'anarkali:any':     { tags: ['silk', 'organza'],                            metres: 5, note: 'Silk or organza — 5 metres of flow.' },

    // SHIRT — ALWAYS cotton/linen first. Shirts are NOT made of saree-silk.
    'shirt:wedding':    { tags: ['linen', 'cotton'],                            metres: 2, note: 'A crisp linen shirt pairs beautifully with formal wedding attire.' },
    'shirt:festival':   { tags: ['cotton', 'linen'],                            metres: 2, note: 'A handloom cotton shirt feels right for festivals — light and breathable.' },
    'shirt:party':      { tags: ['linen', 'cotton'],                            metres: 2, note: 'A crisp linen shirt is the secret to dressing well for parties — sharp but never stiff.' },
    'shirt:formal':     { tags: ['linen', 'cotton'],                            metres: 2, note: 'Linen shirts breathe in Chennai weather. About 2 metres.' },
    'shirt:casual':     { tags: ['cotton', 'linen'],                            metres: 2, note: 'Cotton shirts in 2 metres — easy, breathable, every day.' },
    'shirt:any':        { tags: ['cotton', 'linen'],                            metres: 2, note: 'For a shirt, cotton and linen breathe best.' },

    // DUPATTA
    'dupatta:wedding':  { tags: ['silk', 'organza', 'chanderi'],                metres: 2.5, note: 'A bridal dupatta in silk or organza — 2.5 metres.' },
    'dupatta:festival': { tags: ['silk', 'chanderi'],                           metres: 2.5, note: 'Silk or chanderi dupattas — festive favourites.' },
    'dupatta:any':      { tags: ['organza', 'silk', 'chanderi'],                metres: 2.5, note: 'A dupatta needs 2.5 metres. Organza or chanderi drapes beautifully.' },

    // GOWN / DRESS
    'gown:wedding':     { tags: ['silk', 'brocade'],                            metres: 6, note: 'Wedding gown — 6 metres of silk or brocade.' },
    'gown:party':       { tags: ['silk', 'organza', 'velvet'],                  metres: 6, note: 'Floor-length gown — silk or organza for evening drama.' },
    'gown:any':         { tags: ['silk', 'organza'],                            metres: 6, note: 'Gowns drape best in silk or organza — about 6 metres.' }
  };

  // --------- Destination / location intelligence ---------
  // When customer mentions a destination, the bot infers weather and
  // recommends garments suited to that climate.
  const LOCATION_CONTEXT = {
    // === Cool South Indian hill stations ===
    'ooty':         { weather: 'cool',  temp: '15–22°C', emoji: '🌬️', advice: 'pack layered shirts, warmer kurtas, or a shawl/stole for chilly evenings',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'heavier cotton or linen-cotton blends',
                      tagline: 'Ooty is cool this time of year' },
    'kodaikanal':   { weather: 'cool',  temp: '14–20°C', emoji: '🌬️', advice: 'layered shirts and kurtas feel right; carry a stole for early mornings',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'heavier cotton or wool-blend',
                      tagline: 'Kodai stays cool through the year' },
    'kodai':        { weather: 'cool',  temp: '14–20°C', emoji: '🌬️', advice: 'layered shirts and kurtas feel right; carry a stole for early mornings',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'heavier cotton or wool-blend',
                      tagline: 'Kodai stays cool through the year' },
    'munnar':       { weather: 'cool',  temp: '12–22°C', emoji: '🌬️', advice: 'pack warm cottons and kurta fabric — afternoons warm up but mornings are crisp',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'thick cotton or linen-wool',
                      tagline: 'Munnar swings between crisp mornings and warm afternoons' },
    'coorg':        { weather: 'cool',  temp: '15–25°C', emoji: '🌿', advice: 'breezy shirts for daytime, a stole for misty evenings',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'cotton with a stole on the side',
                      tagline: 'Coorg is misty and mild' },
    'yercaud':      { weather: 'cool',  temp: '15–25°C', emoji: '🌿', advice: 'light layered shirts work beautifully',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'cotton blends',
                      tagline: 'Yercaud is mild and breezy' },

    // === North Indian hill stations (colder) ===
    'shimla':       { weather: 'cold',  temp: '5–18°C',  emoji: '❄️', advice: 'go heavy — thick cottons, wool blends, structured kurtas with shawls',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool-blend or heavy cotton',
                      tagline: 'Shimla is properly cold this season' },
    'manali':       { weather: 'cold',  temp: '0–15°C',  emoji: '❄️', advice: 'wool blends and heavy structured cottons are the call — layering is everything',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy cotton',
                      tagline: 'Manali is genuinely cold' },
    'darjeeling':   { weather: 'cold',  temp: '5–15°C',  emoji: '❄️', advice: 'thick wool blends, layered kurtas, sherwani-style jackets work beautifully',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy cotton',
                      tagline: 'Darjeeling stays cold and crisp' },
    'gangtok':      { weather: 'cold',  temp: '5–18°C',  emoji: '❄️', advice: 'thick fabrics and layered kurtas with shawls work beautifully',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy cotton',
                      tagline: 'Gangtok stays cool' },
    'nainital':     { weather: 'cold',  temp: '5–20°C',  emoji: '❄️', advice: 'wool blends and layered kurtas',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy cotton',
                      tagline: 'Nainital is cool and crisp' },
    'mussoorie':    { weather: 'cold',  temp: '5–22°C',  emoji: '❄️', advice: 'wool blends and layered kurtas',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy cotton',
                      tagline: 'Mussoorie is mountain-cool' },
    'leh':          { weather: 'cold',  temp: '0–20°C',  emoji: '🏔️', advice: 'high altitude — heavy wool, layered everything, structured kurtas',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy structured cotton',
                      tagline: 'Leh-Ladakh is high-altitude cold' },
    'ladakh':       { weather: 'cold',  temp: '0–20°C',  emoji: '🏔️', advice: 'high altitude — heavy wool, layered everything, structured kurtas',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy structured cotton',
                      tagline: 'Ladakh is high-altitude cold' },
    'kashmir':      { weather: 'cold',  temp: '0–20°C',  emoji: '❄️', advice: 'heavy wool blends, layered kurtas, structured shawls',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'wool or heavy structured cotton',
                      tagline: 'Kashmir is genuinely cold' },

    // === Coastal / warm beach destinations ===
    'goa':          { weather: 'warm',  temp: '24–32°C', emoji: '🌴', advice: 'breezy linen shirts and loose cotton kurtas — and a beach saree if you fancy',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'light cotton or mul-cotton',
                      tagline: 'Goa is warm and humid' },
    'pondicherry':  { weather: 'warm',  temp: '26–34°C', emoji: '🌴', advice: 'light cottons and linens — Pondy mornings are perfect for white shirts',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton or linen',
                      tagline: 'Pondy is breezy and warm' },
    'pondy':        { weather: 'warm',  temp: '26–34°C', emoji: '🌴', advice: 'light cottons and linens — Pondy mornings are perfect for white shirts',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton or linen',
                      tagline: 'Pondy is breezy and warm' },
    'puducherry':   { weather: 'warm',  temp: '26–34°C', emoji: '🌴', advice: 'light cottons and linens',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton or linen',
                      tagline: 'Puducherry is breezy and warm' },
    'andaman':      { weather: 'warm',  temp: '26–34°C', emoji: '🏝️', advice: 'lightest cottons — beach saree, kaftan-style kurtas, breezy shirts',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton',
                      tagline: 'Andaman is tropical beach' },
    'kerala':       { weather: 'humid', temp: '26–32°C', emoji: '🌴', advice: 'mul-cottons and Kerala cotton sarees — designed for humidity',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton',
                      tagline: 'Kerala backwaters are humid and warm' },
    'kovalam':      { weather: 'warm',  temp: '26–32°C', emoji: '🌴', advice: 'lightest cottons',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton',
                      tagline: 'Kovalam is tropical beach' },
    'varkala':      { weather: 'warm',  temp: '26–32°C', emoji: '🌴', advice: 'lightest cottons',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton',
                      tagline: 'Varkala is tropical beach' },

    // === Hot / desert ===
    'jaipur':       { weather: 'hot',   temp: '28–38°C', emoji: '🏜️', advice: 'cotton kurta-pyjama sets, light shirts, hand-block prints for the heritage feel',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'pure cotton, hand-block printed',
                      tagline: 'Jaipur is hot and dry' },
    'rajasthan':    { weather: 'hot',   temp: '28–40°C', emoji: '🏜️', advice: 'cotton kurta-pyjama sets, hand-block prints, light long shirts',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'pure cotton',
                      tagline: 'Rajasthan is desert-hot' },
    'jaisalmer':    { weather: 'hot',   temp: '28–42°C', emoji: '🏜️', advice: 'lightest cottons — hand-block printed kurtas',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'pure cotton',
                      tagline: 'Jaisalmer is desert-hot' },
    'jodhpur':      { weather: 'hot',   temp: '28–40°C', emoji: '🏜️', advice: 'cotton kurtas and shirts',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'pure cotton',
                      tagline: 'Jodhpur is hot and dry' },

    // === Urban cities (varied) ===
    'mumbai':       { weather: 'humid', temp: '24–32°C', emoji: '🌆', advice: 'breathable cottons and linens that handle humidity',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton or linen',
                      tagline: 'Mumbai is humid year-round' },
    'delhi':        { weather: 'varied', temp: '5–40°C', emoji: '🌆', advice: 'depends on season — light cottons summer, layered wool blends winter',
                      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'cotton or wool depending on season',
                      tagline: 'Delhi swings hot and cold' },
    'bangalore':    { weather: 'mild',  temp: '18–28°C', emoji: '🌥️', advice: 'light layered cottons — Bangalore is forever moderate',
                      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'cotton',
                      tagline: 'Bangalore stays pleasantly mild' }
  };

  // --------- Season / weather context (NO destination needed) ----------
  // Triggers when customer mentions a season, weather, or temperature
  // WITHOUT specifying a destination. Auto-fills garment + occasion.
  const SEASON_CONTEXT = {
    winter: {
      default_garment: 'kurta', default_occasion: 'festival', fabric_lean: 'heavier weaves like silk-cotton, structured cotton, or brocade',
      advice: 'reach for structured kurtas with shawls, silk-cotton blends, and heavier weaves',
      tagline: "Winter brings out the studio's most-loved structured weaves",
      emoji: '❄️', range: 'cool months · 12–22°C in most of India'
    },
    summer: {
      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'mul-cotton, breezy linen, or hand-loom cottons',
      advice: 'pack mul-cottons, linens, and breezy silhouettes that breathe in the heat',
      tagline: "Summer is RIDH's breeziest season — naturals only",
      emoji: '☀️', range: 'warm months · 28–38°C across India'
    },
    monsoon: {
      default_garment: 'shirt', default_occasion: 'casual', fabric_lean: 'quick-drying cottons and linens',
      advice: 'pick fabrics that dry fast — cottons, linens, earth tones that hide a stray splash',
      tagline: "Monsoon calls for quick-drying naturals",
      emoji: '🌧️', range: 'rainy months · 22–30°C and humid'
    },
    spring: {
      default_garment: 'kurta', default_occasion: 'casual', fabric_lean: 'soft chanderis, light cottons, pastels',
      advice: 'reach for soft chanderis and pastel cotton kurtas — light, optimistic colours',
      tagline: "Spring is for soft pastels and chanderis",
      emoji: '🌸', range: 'mild months · 20–28°C'
    },
    autumn: {
      default_garment: 'kurta', default_occasion: 'festival', fabric_lean: 'silk-cotton blends and warm jewel tones',
      advice: 'silk-cotton kurtas and warm jewel-toned drapes — perfect for the festive season',
      tagline: "Autumn means festive prep at the studio",
      emoji: '🍂', range: 'cooling months · 22–30°C with festive season'
    }
  };

  // --------- Open-ended mood / scenario clues ---------
  // Catches phrases that don't name a garment but imply a need.
  // Examples: "I want something elegant", "office wear", "going to a party"
  const MOOD_TRIGGERS = [
    { match: /\b(elegant|sophisticated|graceful|classy)\b/i, garment: 'saree',    occasion: 'festival' },
    { match: /\b(comfy|comfortable|casual|chill|relax)\b/i,  garment: 'kurta',    occasion: 'casual' },
    { match: /\b(sharp|smart|crisp|tailored)\b/i,            garment: 'shirt',    occasion: 'formal' },
    { match: /\b(traditional|ethnic|heritage|cultural)\b/i,  garment: 'kurta',    occasion: 'festival' },
    { match: /\b(modern|contemporary|chic)\b/i,              garment: 'shirt',    occasion: 'party' },
    { match: /\b(grand|grand-?looking|royal|regal)\b/i,      garment: 'sherwani', occasion: 'wedding' },
    { match: /\b(bridal|wedding\s*day)\b/i,                  garment: 'saree',    occasion: 'wedding' },
    { match: /\b(office\s*wear|work\s*wear|business)\b/i,    garment: 'shirt',    occasion: 'formal' }
  ];

  const COLOURS = ['red', 'maroon', 'crimson', 'wine', 'pink', 'rose', 'blush', 'peach',
                   'blue', 'indigo', 'navy', 'cobalt', 'teal',
                   'green', 'emerald', 'mint', 'olive',
                   'gold', 'golden', 'yellow', 'mustard', 'ochre',
                   'ivory', 'cream', 'white', 'off-white',
                   'black', 'grey', 'gray', 'silver', 'charcoal',
                   'purple', 'violet', 'lavender', 'plum',
                   'orange', 'rust', 'terracotta',
                   'beige', 'tan', 'brown', 'nude', 'sand'];

  // Festive vs Casual fabric map (store's occasion taxonomy — mirrors product tags).
  const OCCASION_FABRICS = {
    festive: {
      label: 'Festive & Wedding', collection: 'festive-wear',
      materials: ['silk', 'organza', 'tissue', 'satin', 'banarasi', 'brocade', 'chanderi', 'tussar', 'net'],
      blurb: 'For festive & wedding wear, our customers love pure silk, organza, tissue, banarasi, brocade, chanderi, tussar, net and richly embroidered fabrics — the pieces that catch the light.'
    },
    casual: {
      label: 'Casual & Daily', collection: 'casual-wear',
      materials: ['cotton', 'mul cotton', 'linen', 'khadi', 'crepe', 'modal'],
      blurb: 'For casual & daily wear, reach for breathable cotton, mul-cotton, linen, khadi and soft crepe — easy to wear, easy to care for.'
    }
  };
  function occasionBucket(occasion) {
    if (occasion === 'wedding' || occasion === 'festival' || occasion === 'party') return 'festive';
    if (occasion === 'casual' || occasion === 'formal') return 'casual';
    return null;
  }

  // ---------------- Fuzzy garment matcher (typo-tolerant) ----------------
  // Maps common misspellings + variations to canonical garment names.
  // Uses Levenshtein distance — "lehanga", "lehnaga", "leganga" all → "lehenga".
  const GARMENT_ALIASES = {
    'saree':      ['saree', 'sari', 'saari', 'silai', 'silay', 'seree', 'sarii'],
    'kurta':      ['kurta', 'kurti', 'kurtha', 'kurthi', 'kurtaa', 'koorta'],
    'lehenga':    ['lehenga', 'lehnga', 'chaniya', 'ghagra', 'leganga', 'lehangha', 'lehngha', 'lengha', 'lengaa', 'legehnga'],
    'blouse':     ['blouse', 'choli', 'blowse', 'blous'],
    'sherwani':   ['sherwani', 'achkan', 'sherwany', 'sharwani', 'shervani'],
    'anarkali':   ['anarkali', 'anarkely', 'anarkly'],
    'shirt':      ['shirt', 'shert', 'sheert'],
    'dupatta':    ['dupatta', 'duppata', 'stole', 'chunni', 'shawl', 'dupata', 'duputta'],
    'gown':       ['gown', 'dress', 'frock', 'maxi', 'gowan', 'goun'],
    'pant':       ['pant', 'pants', 'pantt', 'trouser', 'trousers', 'pyjama', 'pajama', 'churidar', 'palazzo'],
    'bandi':      ['bandi', 'bandy', 'waistcoat', 'bandhi'],
    'bandhgala':  ['bandhgala', 'bandh gala', 'bandgala', 'jodhpuri'],
    'angrakha':   ['angrakha', 'angarkha', 'angarakha', 'angrakh'],
    'longjacket': ['long jacket', 'longjacket', 'longcoat', 'long coat', 'longcoats'],
    'shortjacket':['short jacket', 'shortjacket', 'nehru jacket', 'nehrujacket', 'waistcoat jacket']
  };

  // ---------------- Official size × garment fabric-metre chart (44" width) ----------------
  // Same table shown on the product page's Fabric Estimator accordion.
  // AI concierge uses this whenever it detects garment + gender + size.
  const SIZE_CHART = {
    men: {
      kurta:       { XS: 3.5, S: 3.5, M: 3.5, L: 3.5, XL: 3.5, '2XL': 4.0, '3XL': 4.0, '4XL': 4.5, '5XL': 5.0 },
      pant:        { XS: 2.5, S: 2.5, M: 2.5, L: 3.0, XL: 3.0, '2XL': 3.5, '3XL': 3.5, '4XL': 4.0, '5XL': 4.5 },
      bandi:       { XS: 2.0, S: 2.0, M: 2.0, L: 2.0, XL: 2.5, '2XL': 2.5, '3XL': 3.0, '4XL': 3.5, '5XL': 4.0 },
      sherwani:    { XS: 3.5, S: 3.5, M: 3.5, L: 4.0, XL: 4.0, '2XL': 4.5, '3XL': 4.5, '4XL': 5.0, '5XL': 5.0 },
      bandhgala:   { XS: 3.0, S: 3.0, M: 3.0, L: 3.5, XL: 3.5, '2XL': 4.0, '3XL': 4.0, '4XL': 4.5, '5XL': 4.5 }
    },
    women: {
      kurti:       { XS: 2.0, S: 2.5, M: 2.5, L: 2.5, XL: 2.5, '2XL': 3.0, '3XL': 3.0, '4XL': 3.0, '5XL': 3.0 },
      pant:        { XS: 2.0, S: 2.5, M: 2.5, L: 2.5, XL: 2.5, '2XL': 3.0, '3XL': 3.0, '4XL': 3.0, '5XL': 3.0 },
      blouse:      { XS: 1.0, S: 1.0, M: 1.0, L: 1.0, XL: 1.5, '2XL': 1.5, '3XL': 1.5, '4XL': 1.5, '5XL': 1.5 },
      saree:       { XS: 5.5, S: 5.5, M: 5.5, L: 5.5, XL: 5.5, '2XL': 5.5, '3XL': 5.5, '4XL': 5.5, '5XL': 5.5 },
      lehenga:     { XS: 3.0, S: 4.0, M: 4.0, L: 4.0, XL: 5.0, '2XL': 5.0, '3XL': 5.0, '4XL': 5.0, '5XL': 5.0 },
      gown:        { XS: 4.0, S: 4.5, M: 4.5, L: 4.5, XL: 5.0, '2XL': 5.5, '3XL': 6.0, '4XL': 6.5, '5XL': 7.0 },
      dupatta:     { XS: 2.5, S: 2.5, M: 2.5, L: 2.5, XL: 2.5, '2XL': 2.5, '3XL': 2.5, '4XL': 2.75, '5XL': 2.5 },
      anarkali:    { XS: 4.5, S: 4.5, M: 4.5, L: 5.0, XL: 6.0, '2XL': 6.5, '3XL': 7.0, '4XL': 7.5, '5XL': 8.0 },
      angrakha:    { XS: 3.5, S: 3.5, M: 3.5, L: 4.0, XL: 4.5, '2XL': 5.0, '3XL': 5.5, '4XL': 6.0, '5XL': 6.5 },
      longjacket:  { XS: 3.5, S: 3.5, M: 3.5, L: 4.0, XL: 4.5, '2XL': 5.0, '3XL': 5.5, '4XL': 6.0, '5XL': 6.5 },
      shortjacket: { XS: 2.0, S: 2.0, M: 2.0, L: 2.5, XL: 3.0, '2XL': 3.5, '3XL': 4.0, '4XL': 4.5, '5XL': 5.0 }
    }
  };

  // Map a canonical garment name → the key used in SIZE_CHART for the given gender.
  // Handles: women say "kurta" but chart uses "kurti"; men can say "kurti" but chart uses "kurta".
  function chartKeyFor(garment, gender) {
    if (!garment || !gender) return null;
    const chart = SIZE_CHART[gender];
    if (!chart) return null;
    if (chart[garment]) return garment;
    if (gender === 'women' && garment === 'kurta') return 'kurti';
    if (gender === 'men' && garment === 'kurti') return 'kurta';
    return null;
  }

  // ---------------- Fuzzy occasion matcher (typo-tolerant) ----------------
  // Maps common misspellings to canonical occasions.
  // "wedig", "weding", "weddng" all → "wedding".
  const OCCASION_ALIASES = {
    'wedding':  ['wedding', 'weding', 'wedig', 'weddig', 'weddng', 'wedng', 'marriage', 'marraige', 'mariage', 'bridal', 'bridel', 'engagement', 'engagment', 'sangeet', 'mehendi'],
    'festival': ['festival', 'festvial', 'festval', 'festeval', 'festivel', 'function', 'funktion', 'pongal', 'diwali', 'onam', 'navratri'],
    'party':    ['party', 'parti', 'partey', 'birthday', 'birhtday', 'brithday', 'birtday', 'bday', 'cocktail', 'reunion'],
    'formal':   ['formal', 'formel', 'office', 'ofice', 'corporate', 'business', 'meeting'],
    'casual':   ['casual', 'casul', 'casuel', 'daily', 'dialy', 'everyday', 'regular']
  };
  function fuzzyOccasionMatch(text) {
    const words = text.toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(w => w.length >= 4);
    let best = null, bestDist = 2;  // tight threshold to avoid false positives
    for (const word of words) {
      for (const [occasion, aliases] of Object.entries(OCCASION_ALIASES)) {
        for (const alias of aliases) {
          if (Math.abs(word.length - alias.length) > 2) continue;
          const d = levDist(word, alias);
          if (d < bestDist) { bestDist = d; best = occasion; }
        }
      }
    }
    return best;
  }
  function levDist(a, b) {
    if (a === b) return 0;
    if (!a) return b.length;
    if (!b) return a.length;
    const m = [];
    for (let i = 0; i <= b.length; i++) m[i] = [i];
    for (let j = 0; j <= a.length; j++) m[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        const cost = b[i - 1] === a[j - 1] ? 0 : 1;
        m[i][j] = Math.min(m[i - 1][j] + 1, m[i][j - 1] + 1, m[i - 1][j - 1] + cost);
      }
    }
    return m[b.length][a.length];
  }
  function fuzzyGarmentMatch(text) {
    const words = text.toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(w => w.length >= 3);
    let best = null, bestDist = 3;
    for (const word of words) {
      const variants = [word, word.replace(/s$/, ''), word.replace(/es$/, '')];
      for (const variant of variants) {
        if (variant.length < 3) continue;
        for (const [garment, aliases] of Object.entries(GARMENT_ALIASES)) {
          for (const alias of aliases) {
            if (Math.abs(variant.length - alias.length) > 2) continue;
            const d = levDist(variant, alias);
            if (d < bestDist) { bestDist = d; best = garment; }
          }
        }
      }
    }
    return best;
  }

  // ---------------- Entity parser ----------------
  function parseEntities(text) {
    const lower = text.toLowerCase();
    const out = {};

    // Greeting detection
    if (/^\s*(hi|hii|hiii|hey|hello|hellow|namaste|namaskar|namaskaram|vanakkam|good\s+(morning|evening|afternoon|day))\s*[!?.,]?\s*$/i.test(text.trim())) {
      out.is_greeting = true;
    }

    // Thanks / closing
    if (/^\s*(thanks?|thank\s*you|ty|thx|nice|great|cool|awesome|perfect|ok|okay|sure|done|bye|byee|tata)\s*[!?.,]?\s*$/i.test(text.trim())) {
      out.is_acknowledgement = true;
    }
    // Garment detection — supports plurals (shirts, kurtas, sarees, etc.)
    // Order matters: check specific compounds ("bandh gala", "long jacket") first.
    if (/\b(bandh\s*gala|bandhgala|bandgala|jodhpuri)\b/i.test(lower)) out.garment = 'bandhgala';
    else if (/\b(long\s*jackets?|longjackets?|long\s*coats?)\b/i.test(lower)) out.garment = 'longjacket';
    else if (/\b(short\s*jackets?|shortjackets?|nehru\s*jackets?)\b/i.test(lower)) out.garment = 'shortjacket';
    else if (/\b(angrakhas?|angarkhas?|angarakhas?)\b/i.test(lower)) out.garment = 'angrakha';
    else if (/\b(sarees?|saris?|saaris?|silai)\b/i.test(lower)) out.garment = 'saree';
    else if (/\b(kurtas?|kurtis?|kurthas?)\b/i.test(lower)) out.garment = 'kurta';
    else if (/\b(lehengas?|chaniyas?|ghagras?)\b/i.test(lower)) out.garment = 'lehenga';
    else if (/\b(blouses?|cholis?)\b/i.test(lower)) out.garment = 'blouse';
    else if (/\b(sherwanis?|achkans?)\b/i.test(lower)) out.garment = 'sherwani';
    else if (/\b(anarkalis?)\b/i.test(lower)) out.garment = 'anarkali';
    else if (/\b(shirts?|kurta-shirts?)\b/i.test(lower)) out.garment = 'shirt';
    else if (/\b(dupattas?|duppatas?|stoles?|chunnis?|shawls?)\b/i.test(lower)) out.garment = 'dupatta';
    else if (/\b(gowns?|dresses|dress|frocks?)\b/i.test(lower)) out.garment = 'gown';
    else if (/\b(bandis?|bandhis?|waistcoats?)\b/i.test(lower)) out.garment = 'bandi';
    else if (/\b(pants?|trousers?|pyjamas?|pajamas?|churidars?|palazzos?)\b/i.test(lower)) out.garment = 'pant';

    // ==== Size detection ====
    // XS, S, M, L, XL, 2XL/XXL, 3XL/XXXL, 4XL/XXXXL, 5XL/XXXXXL, "small", "medium", "large",
    // "extra small", "extra large". "S/M/L" alone match only if surrounded by size context.
    (function detectSize() {
      // Try the clearest patterns first
      const explicit = lower.match(/\b(5xl|xxxxxl|4xl|xxxxl|3xl|xxxl|2xl|xxl)\b/i);
      if (explicit) {
        const t = explicit[1].toLowerCase();
        out.size = ({
          '5xl':'5XL','xxxxxl':'5XL',
          '4xl':'4XL','xxxxl':'4XL',
          '3xl':'3XL','xxxl':'3XL',
          '2xl':'2XL','xxl':'2XL'
        })[t];
        return;
      }
      if (/\bxl\b|\bextra[\s-]?large\b/i.test(lower)) { out.size = 'XL'; return; }
      if (/\bxs\b|\bextra[\s-]?small\b/i.test(lower)) { out.size = 'XS'; return; }
      // Word-based sizes — need whole word and not attached to garment count/ordinal
      if (/\blarge\b/i.test(lower))  { out.size = 'L'; return; }
      if (/\bmedium\b/i.test(lower)) { out.size = 'M'; return; }
      if (/\bsmall\b/i.test(lower))  { out.size = 'S'; return; }
      // "size L" / "size M" / "size S" patterns (safer than lone letters)
      const sizeLetter = lower.match(/\bsize\s+([xsml234]{1,4})\b/i);
      if (sizeLetter) {
        const s = sizeLetter[1].toUpperCase();
        if (['XS','S','M','L','XL','2XL','3XL','4XL','5XL'].includes(s)) { out.size = s; return; }
      }
    })();

    // Gender awareness — detected for tonal reference in response
    if (/\b(men|man|gents?|guys?|male|boys?|groom|him|husband|father|dad|brother|son)\b/i.test(lower)) out.gender = 'men';
    else if (/\b(women|woman|ladies|lady|female|girls?|bride|her|wife|mother|mom|sister|daughter)\b/i.test(lower)) out.gender = 'women';

    // "N garments" pattern — e.g. "6 shirts", "3 sarees" → persons=N AND garment=singular
    const qty = lower.match(/(\d+)\s+(shirts?|kurtas?|kurtis?|sarees?|saris?|saaris?|lehengas?|sherwanis?|blouses?|dupattas?|gowns?|dresses|dress|frocks?|anarkalis?|stoles?|shawls?)/i);
    if (qty) {
      const n = parseInt(qty[1]);
      if (n >= 2 && n <= 50) out.persons = n;
      // Force garment from matched word (in case order matters)
      const word = qty[2].toLowerCase().replace(/(es|s)$/, '');
      const garmentMap = {
        'shirt': 'shirt', 'kurta': 'kurta', 'kurti': 'kurta',
        'saree': 'saree', 'sari': 'saree', 'saari': 'saree',
        'lehenga': 'lehenga', 'sherwani': 'sherwani', 'blouse': 'blouse',
        'dupatta': 'dupatta', 'gown': 'gown', 'dress': 'gown', 'frock': 'gown',
        'anarkali': 'anarkali', 'stole': 'dupatta', 'shawl': 'dupatta'
      };
      const matchedGarment = garmentMap[word];
      if (matchedGarment) out.garment = matchedGarment;
    }

    if (/\b(wedding|bridal|bride|reception|sangeet|mehendi|engagement|nikah|marriage)\b/.test(lower)) out.occasion = 'wedding';
    else if (/\b(diwali|festival|pongal|onam|eid|holi|raksha|navratri|festive|pooja|puja|function|ceremony|anniversary|housewarming|naming|griha)\b/.test(lower)) out.occasion = 'festival';
    else if (/\b(office|work|formal|business|corporate|meeting|interview|conference)\b/.test(lower)) out.occasion = 'formal';
    else if (/\b(party|cocktail|evening|night-?out|birthday|b-?day|get-?together|gathering|social|club|reunion|hangout|brunch|dinner)\b/.test(lower)) out.occasion = 'party';

    // ==== Age + wearer detection ====
    // "3 yrs old", "5 year old", "8 years", "4yo", "for my 6 year old daughter"
    const ageMatch = lower.match(/\b(\d{1,2})\s*(?:\+|yrs?|years?|yo|y\.o\.|year[\s-]?old|months?\s*old|month)\b/);
    if (ageMatch) {
      const a = parseInt(ageMatch[1]);
      if (a >= 0 && a <= 90) {
        out.wearer_age = a;
        // "3 month" or "3 months old" — treat as infant
        if (/months?\s*old|month\b/.test(ageMatch[0])) out.wearer_age = 0;
      }
    }
    // Relationship hints — distinguishes whose body the fabric is being stitched for
    const childRel = /\b(kid|kids|child|children|baby|babies|infant|toddler|son|daughter|boy|girl|niece|nephew|grandson|granddaughter|grandchild|little\s+one|youngster)\b/i;
    const adultRel = /\b(my\s*self|myself|husband|wife|hubby|father|mother|dad|mom|mum|uncle|aunty|aunt|sister|brother|friend|colleague|fiance|fiancee|partner|spouse|bride|groom)\b/i;
    if (childRel.test(lower)) out.relationship = 'child';
    else if (adultRel.test(lower)) out.relationship = 'adult';
    // Compute final wearer type — age wins if both present
    if (out.wearer_age != null) {
      if (out.wearer_age <= 14) out.wearer = 'child';
      else if (out.wearer_age <= 18) out.wearer = 'teen';
      else out.wearer = 'adult';
    } else if (out.relationship === 'child') {
      // "for my baby/toddler/little one" → child even without explicit age
      if (/\b(baby|babies|infant|toddler|little\s+one|small\s+(kid|child)|tiny|young\s+(kid|child)|preschool)\b/i.test(lower)) {
        out.wearer = 'child';
        if (out.wearer_age == null) out.wearer_age = 4;
      }
    }

    // Season / weather detection — auto-fills garment if not already set
    if (/\b(winter|cold|chilly|christmas|new\s*year)\b/i.test(lower)) {
      out.season = 'winter';
      if (!out.occasion) out.occasion = SEASON_CONTEXT.winter.default_occasion;
      if (!out.garment) out.garment = SEASON_CONTEXT.winter.default_garment;
    } else if (/\b(summer|summery|hot|warm|tropical|sunny|heat)\b/i.test(lower)) {
      out.season = 'summer';
      if (!out.occasion) out.occasion = SEASON_CONTEXT.summer.default_occasion;
      if (!out.garment) out.garment = SEASON_CONTEXT.summer.default_garment;
    } else if (/\b(monsoon|rainy|rains?|wet)\b/i.test(lower)) {
      out.season = 'monsoon';
      if (!out.occasion) out.occasion = SEASON_CONTEXT.monsoon.default_occasion;
      if (!out.garment) out.garment = SEASON_CONTEXT.monsoon.default_garment;
    } else if (/\b(spring|march|april)\b/i.test(lower)) {
      out.season = 'spring';
      if (!out.occasion) out.occasion = SEASON_CONTEXT.spring.default_occasion;
      if (!out.garment) out.garment = SEASON_CONTEXT.spring.default_garment;
    } else if (/\b(autumn|fall|october|november)\b/i.test(lower)) {
      out.season = 'autumn';
      if (!out.occasion) out.occasion = SEASON_CONTEXT.autumn.default_occasion;
      if (!out.garment) out.garment = SEASON_CONTEXT.autumn.default_garment;
    } else if (/\b(casual|daily|everyday|college|home|regular|usual|normal|outing|holiday|vacation|trip|travel)\b/.test(lower)) {
      out.occasion = 'casual';
    }

    // Mood / scenario clues — fills garment + occasion from descriptive phrases
    if (!out.garment || !out.occasion) {
      for (const trigger of MOOD_TRIGGERS) {
        if (trigger.match.test(lower)) {
          if (!out.garment) out.garment = trigger.garment;
          if (!out.occasion) out.occasion = trigger.occasion;
          out.mood_matched = true;
          break;
        }
      }
    }

    // Fuzzy garment matching — catches typos like "lehanga", "lehnaga", "sherwany"
    if (!out.garment && !out.is_greeting && !out.is_acknowledgement) {
      const fuzzy = fuzzyGarmentMatch(text);
      if (fuzzy) {
        out.garment = fuzzy;
        out.was_fuzzy = true;
      }
    }

    // Fuzzy occasion matching — catches typos like "wedig", "weding", "birhtday"
    if (!out.occasion && !out.is_greeting && !out.is_acknowledgement) {
      const fuzzyOcc = fuzzyOccasionMatch(text);
      if (fuzzyOcc) {
        out.occasion = fuzzyOcc;
        out.was_fuzzy_occasion = true;
      }
    }

    for (const c of COLOURS) {
      const re = new RegExp('\\b' + c.replace('-', '[-\\s]?') + '\\b');
      if (re.test(lower)) { out.colour = c; break; }
    }

    // Location detection — auto-infers garment, occasion, and fabric lean
    for (const loc of Object.keys(LOCATION_CONTEXT)) {
      const re = new RegExp('\\b' + loc + '\\b');
      if (re.test(lower)) {
        out.location = loc;
        const ctx = LOCATION_CONTEXT[loc];
        // Set defaults if not already set
        if (!out.garment) out.garment = ctx.default_garment;
        if (!out.occasion) out.occasion = ctx.default_occasion;
        break;
      }
    }

    // Metres
    const m = lower.match(/(\d+(?:\.\d+)?)\s*(metres?|meters?|m)\b/);
    if (m) out.metres = parseFloat(m[1]);

    // Persons / quantity (for stitching multiple garments)
    const persons = lower.match(/(?:for\s+|stitch\s+for\s+|need\s+for\s+)?(\d+)\s*(?:persons?|people|members?|guests?|kids?|adults?|family|friends?|gents?|ladies)/);
    if (persons) {
      const n = parseInt(persons[1]);
      if (n >= 2 && n <= 50) out.persons = n;
    }

    // Detect that the input is a follow-up QUESTION (not a new query)
    const isQuestion = /\?$|^\s*(how|what|why|when|where|can|do|does|will|tell|explain|is\s+it|are\s+they|which)\b/i.test(text.trim());
    out.is_question = isQuestion;

    // Pricing / care / delivery intents
    if (/\b(price|cost|expensive|cheap|affordable|budget|costing)\b/i.test(lower)) out.intent_price = true;
    if (/\b(wash|care|maintain|iron|shrink|dry\s*clean|machine\s*wash|hand\s*wash)\b/i.test(lower)) out.intent_care = true;
    if (/\b(delivery|shipping|courier|deliver|how\s+long|how\s+soon|when\s+will|how\s+many\s+days)\b/i.test(lower)) out.intent_delivery = true;
    if (/\b(swatch|sample|see\s+(the\s+)?fabric|touch\s+the\s+fabric)\b/i.test(lower)) out.intent_swatch = true;

    // Budget — handles "₹2000", "rs 2500", "under 3000", "around 1500", "₹500-2000"
    const bRange = lower.match(/(?:₹|rs\.?|inr)?\s*(\d{2,5})\s*[-–to]+\s*(?:₹|rs\.?|inr)?\s*(\d{2,5})/);
    if (bRange) {
      out.budget_min = parseInt(bRange[1]);
      out.budget_max = parseInt(bRange[2]);
    } else {
      const bSingle = lower.match(/(?:under|less than|below|around|about|approx|max|maximum|budget)?\s*(?:₹|rs\.?|inr)\s*(\d{2,5})/);
      if (bSingle) out.budget_max = parseInt(bSingle[1]);
    }

    return out;
  }

  // ---------------- UI helpers ----------------
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function formatMoney(cents) {
    if (window.formatMoney) return window.formatMoney(cents);
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }
  function scrollChatToBottom() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }
  function showTyping(show) { if (typingEl) typingEl.hidden = !show; }

  // Plain text of a bot bubble, for the AI's conversation memory.
  function htmlToText(html) {
    const d = document.createElement('div');
    d.innerHTML = String(html || '');
    return (d.textContent || '').replace(/\s+/g, ' ').trim();
  }
  function pushHistory(role, content) {
    if (!content) return;
    state.history.push({ role: role, content: content });
    if (state.history.length > 20) state.history = state.history.slice(-20);
  }

  function appendUser(text) {
    pushHistory('user', String(text || ''));
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;justify-content:flex-end;';
    wrap.innerHTML = `
      <div style="max-width: 80%; padding: 12px 16px; background: #B8923F; color: #FFFFFF; font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.45;">
        ${escapeHtml(text)}
      </div>`;
    messagesEl.appendChild(wrap);
    scrollChatToBottom();
  }

  function appendBot(html) {
    pushHistory('assistant', htmlToText(html));
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;gap:10px;align-items:flex-start;';
    wrap.innerHTML = `
      <span style="flex:none;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#B8923F;color:#FFFFFF;font-family:'Fraunces',Georgia,serif;font-weight:500;font-size:14px;">R</span>
      <div style="max-width: 80%; padding: 12px 16px; background: #FBFAF7; border: 1px solid #E0D6C5; color: #1A1614; font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.55;">
        ${html}
      </div>`;
    messagesEl.appendChild(wrap);
    scrollChatToBottom();
  }

  // Placeholder for products without images
  const PLACEHOLDER_PALETTES = [
    ['#8B2434', '#3A0F18'], ['#B8923F', '#4A3818'], ['#1A4D3A', '#0A2620'],
    ['#2E4A6B', '#10202F'], ['#5A2E1F', '#2A1410'], ['#6E5B85', '#2D243A']
  ];
  function placeholderSvg(title) {
    const letter = (title || 'R').trim().charAt(0).toUpperCase();
    let h = 0; for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) % PLACEHOLDER_PALETTES.length;
    const [a, b] = PLACEHOLDER_PALETTES[Math.abs(h) % PLACEHOLDER_PALETTES.length];
    const gid = 'cg' + Math.random().toString(36).slice(2, 8);
    return `
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;display:block;" preserveAspectRatio="xMidYMid slice">
        <defs><linearGradient id="${gid}" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="${a}"/><stop offset="100%" stop-color="${b}"/></linearGradient></defs>
        <rect width="100" height="100" fill="url(#${gid})"/>
        <text x="50" y="62" text-anchor="middle" font-family="Fraunces, Georgia, serif" font-size="40" font-weight="300" fill="rgba(245,239,230,0.85)">${letter}</text>
      </svg>`;
  }

  function appendProducts(products, metres) {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;gap:10px;align-items:flex-start;';
    const cards = products.map((p) => {
      const img = p.image ? `<img src="${escapeHtml(p.image)}" alt="" style="width:100%;height:100%;object-fit:cover;">` : placeholderSvg(p.title || 'R');
      const m = (p.metres != null) ? p.metres : metres;
      // Placeholder prices (no real price in the IMS sheet) must never be shown as a
      // real rate: ₹10,000 = legacy placeholder, ₹111 = current one (boss, 2026-07-29).
      // ⚠️ Keep in sync with PLACEHOLDER_* in wati-webhook/lib/concierge.js.
      const realPrice = p.price && p.price < 9000 && p.price !== 111;
      const priceLabel = realPrice ? (formatMoney(p.price * 100) + ' / metre') : 'Price on request';
      // No real price yet → offer a WhatsApp enquiry instead of an Add-to-cart button,
      // so nobody can buy at the placeholder rate. Mirrors sections/main-product.liquid.
      const waMsg = encodeURIComponent(
        "Hi RIDH — I'd like to know the price for " + p.title + ' (' + location.origin + '/products/' + p.handle + ')'
      );
      const action = realPrice
        ? `<button type="button" data-add-to-cart data-handle="${escapeHtml(p.handle)}" data-qty="${m}" data-title="${escapeHtml(p.title)}"
              style="padding:6px 12px;font-family:'Inter',sans-serif;font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:#0A0806;background:#B8923F;border:0;cursor:pointer;">Add ${m}m to cart →</button>`
        : `<a href="https://wa.me/918124440211?text=${waMsg}" target="_blank" rel="noopener"
              style="display:inline-block;padding:6px 12px;font-family:'Inter',sans-serif;font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:#0A0806;background:#B8923F;text-decoration:none;">Enquire price →</a>`;
      return `
        <article style="display:flex;gap:12px;padding:10px;background:#FFFFFF;border:1px solid #E0D6C5;align-items:center;">
          <a href="${escapeHtml(p.url)}" style="flex:none;width:64px;height:64px;overflow:hidden;background:#FBFAF7;border:1px solid #E0D6C5;">
            ${img}
          </a>
          <div style="flex:1;min-width:0;">
            <a href="${escapeHtml(p.url)}" style="display:block;font-family:'Fraunces',Georgia,serif;font-weight:400;font-size:13px;line-height:1.3;color:#1A1614;text-decoration:none;margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(p.title)}</a>
            <div style="font-family:'Inter',sans-serif;font-size:11px;color:#6B6359;margin-bottom:6px;">${priceLabel} · ${m} m needed</div>
            ${action}
          </div>
        </article>`;
    }).join('');
    wrap.innerHTML = `
      <span style="flex:none;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#B8923F;color:#FFFFFF;font-family:'Fraunces',Georgia,serif;font-weight:500;font-size:14px;">R</span>
      <div style="flex:1;display:flex;flex-direction:column;gap:8px;">${cards}</div>`;
    messagesEl.appendChild(wrap);
    scrollChatToBottom();

    // Wire up Add to Cart buttons
    wrap.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.addEventListener('click', () => handleAddToCart(btn));
    });
  }

  // Metres for a Claude-linked product: prefer its data-metres, else read the
  // nearby text, else a sensible default of 3.
  function metresNear(anchor) {
    const attr = parseFloat(anchor.getAttribute('data-metres'));
    if (attr >= 0.5 && attr <= 20) return Math.round(attr * 10) / 10;
    const block = anchor.closest('p, li') || anchor.parentElement;
    const t = block ? String(block.textContent || '') : '';
    const m = t.match(/(\d+(?:\.\d+)?)\s*(?:m\b|met(?:re|er)s?)/i);
    if (m) { const v = parseFloat(m[1]); if (v >= 0.5 && v <= 20) return Math.round(v * 10) / 10; }
    return 3;
  }

  // Scan a Claude reply for product links and render an Add-to-cart card for each.
  async function renderClaudeProducts(replyHtml) {
    try {
      const tmp = document.createElement('div');
      tmp.innerHTML = String(replyHtml || '');
      const seen = new Set();
      const wanted = [];
      tmp.querySelectorAll('a[href*="/products/"]').forEach((a) => {
        const mm = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        if (!mm) return;
        const handle = mm[1];
        if (seen.has(handle) || wanted.length >= 5) return;
        seen.add(handle);
        wanted.push({ handle: handle, metres: metresNear(a) });
      });
      if (!wanted.length) return;
      const products = [];
      for (const w of wanted) {
        try {
          const r = await fetch('/products/' + encodeURIComponent(w.handle) + '.js');
          if (!r.ok) continue;
          const p = await r.json();
          products.push({
            handle: w.handle,
            title: p.title,
            price: (p.price || 0) / 100,
            image: p.featured_image || (p.images && p.images[0]) || null,
            url: '/products/' + w.handle,
            metres: w.metres
          });
        } catch (e) { /* skip this one */ }
      }
      if (products.length) appendProducts(products, products[0].metres || 3);
    } catch (e) { /* no cards */ }
  }

  // ---------------- Add to cart from chat ----------------
  async function handleAddToCart(btn) {
    const handle = btn.dataset.handle;
    const qty = parseFloat(btn.dataset.qty) || 1;
    const title = btn.dataset.title;
    const original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      // Get variant id from product json
      const pRes = await fetch(`/products/${handle}.js`);
      if (!pRes.ok) throw new Error('product fetch');
      const product = await pRes.json();
      const variantId = product.variants[0].id;
      const qtyInt = Math.max(1, Math.ceil(qty)); // Shopify requires int qty for variants sold per metre

      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qtyInt }] })
      });
      if (!cartRes.ok) throw new Error('cart add');

      btn.textContent = 'Added ✓';
      btn.style.background = '#1A4D3A';
      btn.style.color = '#F5EFE6';
      if (window.refreshCartCount) window.refreshCartCount();

      appendBot(`<strong>${escapeHtml(title)}</strong> is now in your cart — ${qtyInt} metre${qtyInt > 1 ? 's' : ''}. Want me to suggest a matching <em>blouse fabric</em> or a <em>dupatta</em>?`);
    } catch (err) {
      console.error('[concierge]', err);
      btn.disabled = false;
      btn.textContent = 'Try again';
      appendBot("Hmm, I couldn't add that to the cart automatically. <a href=\"/products/" + escapeHtml(handle) + "\" style=\"color:#B8923F;text-decoration:underline;\">Open the product page</a> and add it from there.");
    }
  }

  // ---------------- Product search ----------------
  // Fetch a LARGER pool and randomly select N — gives variety across
  // multiple recommendations even for the same query.
  function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  async function shopifySearch(query, limit = 4) {
    const url = new URL('/search/suggest.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    // Fetch up to 10 results, then pick N randomly for variety
    url.searchParams.set('resources[limit]', '10');
    try {
      const res = await fetch(url.toString());
      if (!res.ok) return [];
      const data = await res.json();
      const all = (data.resources && data.resources.results && data.resources.results.products || []).map((p) => ({
        title: p.title, handle: p.handle, url: p.url, image: p.image,
        price: p.price ? parseFloat(p.price) : null
      }));
      return shuffle(all).slice(0, limit);
    } catch (err) { return []; }
  }
  async function fetchPopular(limit = 4) {
    try {
      // Random page for variety across calls
      const page = Math.floor(Math.random() * 5) + 1;
      const res = await fetch(`/products.json?limit=20&page=${page}`);
      if (!res.ok) return [];
      const data = await res.json();
      const all = (data.products || []).map((p) => ({
        title: p.title, handle: p.handle, url: '/products/' + p.handle,
        image: p.images && p.images[0] ? p.images[0].src : '',
        price: p.variants && p.variants[0] ? parseFloat(p.variants[0].price) : null
      }));
      return shuffle(all).slice(0, limit);
    } catch (err) { return []; }
  }

  // ---------------- Recommendation builder ----------------
  // Generates an editorial, sales-focused response: validation opener →
  // styling tip → cost estimate → tailor buffer note → visit CTA →
  // product cards → "YOUR RIDH FABRIC PLAN" structured summary card.
  function appendFabricPlan(planRows) {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;gap:10px;align-items:flex-start;margin-top:4px;';
    const rowsHtml = planRows.map((r) => `
      <div style="display:grid;grid-template-columns:1fr auto;gap:12px;padding:14px 16px;border-top:1px solid rgba(184,146,63,0.18);">
        <div>
          <div style="font-family:'Fraunces',Georgia,serif;font-size:18px;font-weight:300;color:#1A1614;letter-spacing:-0.01em;line-height:1.1;">${escapeHtml(r.fabric)}</div>
          <div style="font-family:'Inter',sans-serif;font-size:10px;font-weight:500;letter-spacing:0.18em;text-transform:uppercase;color:rgba(184,146,63,0.85);margin-top:4px;">${escapeHtml(r.label)}</div>
        </div>
        <div style="text-align:right;">
          <div style="font-family:'Inter',sans-serif;font-size:13px;color:#1A1614;font-weight:500;">${escapeHtml(r.metres)}</div>
          <div style="font-family:'Inter',sans-serif;font-size:12px;color:#6B6359;margin-top:2px;">${escapeHtml(r.priceLine)}</div>
        </div>
      </div>
    `).join('');
    wrap.innerHTML = `
      <span style="flex:none;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#B8923F;color:#FFFFFF;font-family:'Fraunces',Georgia,serif;font-weight:500;font-size:14px;">R</span>
      <div style="flex:1;background:rgba(184,146,63,0.06);border:1px solid rgba(184,146,63,0.3);">
        <header style="display:flex;justify-content:space-between;align-items:center;padding:14px 16px;background:rgba(184,146,63,0.12);">
          <span style="font-family:'Inter',sans-serif;font-size:10px;font-weight:500;letter-spacing:0.22em;text-transform:uppercase;color:#B8923F;">Your RIDH Fabric Plan</span>
          <span style="font-family:'Inter',sans-serif;font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:rgba(184,146,63,0.85);">${planRows.length} fabric${planRows.length > 1 ? 's' : ''}</span>
        </header>
        ${rowsHtml}
        <footer style="padding:12px 16px;border-top:1px solid rgba(184,146,63,0.18);font-family:'Inter',sans-serif;font-size:11px;color:#8B6F2C;font-style:italic;">Ranges include a tailor buffer. Confirm exact metres with your tailor before ordering.</footer>
      </div>`;
    messagesEl.appendChild(wrap);
    scrollChatToBottom();
  }

  // Adjust scenario + rule when the fabric is for a child / teen.
  // Scales metres, switches to softer fabrics, rewrites styling tip + opener.
  function adjustForChild(scenario, rule, age, garment) {
    const a = age == null ? 4 : age;
    // Metre scale by age (rough): infant 25%, toddler 30%, kid 40-55%, pre-teen 70%, teen 85%
    const scale = a <= 1 ? 0.25 : a <= 3 ? 0.32 : a <= 5 ? 0.40 : a <= 8 ? 0.55 : a <= 12 ? 0.70 : 0.85;
    const baseMin = scenario.metres_min || rule.metres || 2;
    const baseMax = scenario.metres_max || rule.metres || 2;
    const childMin = Math.max(0.75, parseFloat((baseMin * scale).toFixed(2)));
    const childMax = Math.max(1.0,  parseFloat((baseMax * scale).toFixed(2)));

    // Kid-friendly fabric families — soft, breathable, gentle on skin.
    // Heavy brocade, velvet, stiff organza removed.
    const childTags = ['cotton', 'chanderi', 'silk', 'mul', 'organza'];

    const ageLabel = a <= 1 ? `${a == 0 ? 'newborn' : '1-year-old'}`
                    : a <= 3 ? `${a}-year-old toddler`
                    : a <= 8 ? `${a}-year-old`
                    : a <= 12 ? `${a}-year-old`
                    : `${a}-year-old teen`;

    const openerByGarment = {
      lehenga:  `For a ${ageLabel}, a soft chanderi or light silk lehenga in a happy colour drapes beautifully — comfortable, breathable, easy to twirl in 🌸`,
      kurta:    `For a ${ageLabel}, a soft hand-loom cotton or chanderi kurta is gentle on skin and easy to wash 🌿`,
      saree:    `For a ${ageLabel}, a small chanderi or mul-cotton saree (half-saree style) works beautifully — soft and easy to drape 🌸`,
      shirt:    `For a ${ageLabel}, soft pre-shrunk cotton or mul-cotton is the safest, most comfortable pick ☁️`,
      gown:     `For a ${ageLabel}, a flowing chanderi or mul-cotton frock in a bright happy colour is the cutest pick 🌸`,
      sherwani: `For a ${ageLabel}, a light silk or cotton-silk sherwani in soft tones is comfortable and adorable for festive looks ✨`,
      anarkali: `For a ${ageLabel}, a chanderi or light silk anarkali flows beautifully — soft on skin, easy to wear 🌸`,
      blouse:   `For a ${ageLabel}, a tiny silk or cotton blouse is just half a metre or less — soft pieces only 🌸`,
      dupatta:  `For a ${ageLabel}, a small chanderi or organza dupatta in a light shade adds the perfect finish 🌸`
    };

    return {
      ...scenario,
      metres_min: childMin,
      metres_max: childMax,
      buffer: 0.25,
      tags: childTags,
      label: 'LITTLE ' + (scenario.label || (garment || 'OUTFIT').toUpperCase()),
      opener: openerByGarment[garment] || `For a ${ageLabel}, soft chanderi, mul-cotton, or light silk is the kindest, comfiest pick 🌸`,
      styling_tip: 'Bright, joyful colours — coral, peach, mint, butter yellow, soft pink, sky blue. Avoid scratchy weaves and heavy zari near the skin.',
      buyer_hook: `Kid-friendly fabrics at RIDH start at ₹350/m (cotton) and ₹650/m (chanderi). All pre-shrunk, gentle on skin, machine-washable.`,
      _child_rule_tags: childTags
    };
  }

  async function makeRecommendation() {
    const ruleKey = `${state.garment}:${state.occasion}`;
    const altKey  = `${state.garment}:any`;
    let rule = RULES[ruleKey] || RULES[altKey] || { tags: ['cotton', 'silk'], metres: 6, note: '' };
    // Comprehensive scenario fallback — try every variant before giving up
    let scenario = SCENARIOS[ruleKey]
                  || SCENARIOS[`${state.garment}:any`]
                  || SCENARIOS[`${state.garment}:casual`]
                  || SCENARIOS[`${state.garment}:festival`]
                  || SCENARIOS[`${state.garment}:party`]
                  || SCENARIOS[`${state.garment}:wedding`]
                  || SCENARIOS[`shirt:casual`]; // ultimate fallback so opener is never empty

    // === Child / teen wearer override ===
    // When wearer is a child, the adult scenario doesn't apply — heavy
    // velvet/brocade, party-evening tips, and 7m of fabric are all wrong.
    if (state.wearer === 'child' && scenario) {
      scenario = adjustForChild(scenario, rule, state.wearer_age, state.garment);
      rule = { ...rule, tags: scenario._child_rule_tags || rule.tags };
    }

    // Range metres: prefer scenario (with buffer) over single value
    let metresMin, metresMax, label, buffer;
    if (scenario) {
      metresMin = scenario.metres_min;
      metresMax = scenario.metres_max;
      label = scenario.label;
      buffer = scenario.buffer;
    } else {
      metresMin = (state.metres || rule.metres) - 0.25;
      metresMax = (state.metres || rule.metres) + 0.25;
      label = (state.garment || 'FABRIC').toUpperCase();
      buffer = 0.25;
    }
    if (state.metres) {
      // Override with user-specified, but keep small buffer
      metresMin = Math.max(0.5, state.metres - 0.25);
      metresMax = state.metres + 0.25;
    }

    // === OFFICIAL SIZE CHART OVERRIDE — highest priority ===
    // If garment + gender + size all detected, use the exact value from Ridh's
    // fabric estimator chart (Men/Women × XS-5XL). +0.25m tailor buffer.
    let sizeChartUsed = null;
    if (state.garment && state.gender && state.size) {
      const key = chartKeyFor(state.garment, state.gender);
      const cell = key && SIZE_CHART[state.gender] && SIZE_CHART[state.gender][key]
                   && SIZE_CHART[state.gender][key][state.size];
      if (cell != null) {
        metresMin = cell;
        metresMax = parseFloat((cell + 0.25).toFixed(2));
        sizeChartUsed = { garment: key, gender: state.gender, size: state.size, metres: cell };
      }
    }

    // Scale for multiple persons (e.g. "stitching for 6 people")
    if (state.persons && state.persons > 1) {
      metresMin = parseFloat((metresMin * state.persons).toFixed(2));
      metresMax = parseFloat((metresMax * state.persons).toFixed(2));
    }

    // Search products — try each tag in order; combine with colour if specified
    let products = [];
    let tagUsed = rule.tags[0];
    if (state.colour) {
      for (const tag of rule.tags) {
        products = await shopifySearch(`${state.colour} ${tag}`, 4);
        if (products.length) { tagUsed = tag; break; }
      }
      if (!products.length) products = await shopifySearch(state.colour, 4);
    }
    if (!products.length) {
      for (const tag of rule.tags) {
        products = await shopifySearch(tag, 4);
        if (products.length) { tagUsed = tag; break; }
      }
    }
    if (!products.length) products = await fetchPopular(4);

    // ============= Build rich response =============
    let opener;
    const personsLine = state.persons && state.persons > 1
      ? ` Stitching <strong>${state.persons} pieces</strong> — let's plan it together.`
      : '';
    // For child wearers, the opener already starts with "For a 3-year-old..."
    // so suppress the men/women prefix — it would read awkwardly.
    const genderPrefix = state.wearer === 'child' ? ''
                       : state.gender === 'men' ? "For men's wear, "
                       : state.gender === 'women' ? "For women's wear, " : '';

    // Size-chart callout — takes priority when we've matched the official chart
    if (sizeChartUsed) {
      const g = sizeChartUsed.gender === 'men' ? "Men's" : "Women's";
      const displayName = ({
        kurta: 'Kurta', kurti: 'Kurti', pant: 'Pant', bandi: 'Bandi',
        sherwani: 'Sherwani', bandhgala: 'Bandh Gala',
        blouse: 'Blouse', saree: 'Saree', lehenga: 'Lehenga',
        gown: 'Gown', dupatta: 'Dupatta', anarkali: 'Anarkali',
        angrakha: 'Angrakha', longjacket: 'Long Jacket', shortjacket: 'Short Jacket'
      })[sizeChartUsed.garment] || sizeChartUsed.garment;
      opener = `Per our fabric estimator, a size <strong>${sizeChartUsed.size}</strong> ${g} <strong>${displayName}</strong> needs exactly <strong>${sizeChartUsed.metres} metres</strong> (44&Prime; width). I've added a small tailor buffer.${personsLine}`;
      if (scenario) opener += ` ${scenario.opener.charAt(0).toUpperCase() + scenario.opener.slice(1)}.`;
    } else if (state.location && LOCATION_CONTEXT[state.location]) {
      const locCtx = LOCATION_CONTEXT[state.location];
      opener = `${locCtx.tagline} ${locCtx.emoji} (typically ${locCtx.temp}). Most travellers ${locCtx.advice} — so I'd lean towards <strong>${locCtx.fabric_lean}</strong> for your ${state.garment}.${personsLine}`;
      if (scenario) opener += ` ${scenario.opener.charAt(0).toUpperCase() + scenario.opener.slice(1)}.`;
    } else if (state.season && SEASON_CONTEXT[state.season]) {
      const seasCtx = SEASON_CONTEXT[state.season];
      opener = `${seasCtx.tagline} ${seasCtx.emoji} (${seasCtx.range}). Most regulars ${seasCtx.advice} — I'd recommend a <strong>${state.garment}</strong> in <strong>${seasCtx.fabric_lean}</strong>.${personsLine}`;
      if (scenario) opener += ` ${scenario.opener.charAt(0).toUpperCase() + scenario.opener.slice(1)}.`;
    } else if (scenario) {
      opener = `${genderPrefix}${scenario.opener.charAt(0).toUpperCase() + scenario.opener.slice(1)}.${personsLine}`;
    } else {
      opener = `Lovely choice — let me share my picks from the studio.`;
    }

    const colourLine = state.colour ? ` in <strong>${escapeHtml(state.colour)}</strong>` : '';

    // Cost estimate using actual product prices
    const validPrices = products.filter(p => p.price > 0).map(p => p.price);
    let costEstimateBlock = '';
    if (validPrices.length) {
      const minPrice = Math.min(...validPrices);
      const maxPrice = Math.max(...validPrices);
      const costMin = Math.round(minPrice * metresMin);
      const costMax = Math.round(maxPrice * metresMax);
      costEstimateBlock = `
        <div style="margin-top:10px;padding:12px;background:rgba(184,146,63,0.08);border-left:2px solid #B8923F;">
          <div style="font-family:'Inter',sans-serif;font-size:10px;font-weight:500;letter-spacing:0.18em;text-transform:uppercase;color:#B8923F;margin-bottom:6px;">💰 Quick cost estimate</div>
          <div style="font-family:'Inter',sans-serif;font-size:13px;color:#1A1614;">
            <strong>${metresMin}m – ${metresMax}m</strong> &middot; approx <strong>₹${costMin.toLocaleString('en-IN')} – ₹${costMax.toLocaleString('en-IN')}</strong>${colourLine}
          </div>
        </div>`;
    }

    const stylingTipBlock = scenario && scenario.styling_tip ? `
      <div style="margin-top:10px;font-size:13px;line-height:1.55;">
        <span style="display:inline-block;font-family:'Inter',sans-serif;font-size:10px;font-weight:500;letter-spacing:0.18em;text-transform:uppercase;color:#B8923F;margin-right:6px;">💡 Styling tip</span>
        <span style="color:#3E332B;">${scenario.styling_tip}</span>
      </div>` : '';

    const buyerHookBlock = scenario && scenario.buyer_hook ? `
      <div style="margin-top:10px;font-size:13px;line-height:1.55;color:#3E332B;">
        <span style="color:#B8923F;">🧵</span> ${scenario.buyer_hook}
      </div>` : '';

    const visitCta = `
      <div style="margin-top:14px;padding-top:12px;border-top:1px solid rgba(184,146,63,0.15);font-size:12px;color:#6B6359;">
        📍 Visit <strong style="color:#1A1614;">RIDH — The Fabric Studio, Egmore, Chennai</strong> to touch the fabrics, or tap <em style="color:#B8923F;">Add to cart</em> below to reserve them online.
      </div>`;

    appendBot(`${opener}${stylingTipBlock}${costEstimateBlock}${buyerHookBlock}${visitCta}`);
    await delay(400);

    // Products with metre-range button
    const avgMetres = Math.round((metresMin + metresMax) / 2 * 10) / 10;
    appendProducts(products, avgMetres);
    state.showedProducts = true;
    state.lastTagUsed = tagUsed;
    await delay(400);

    // Fabric Plan summary card (premium reveal)
    if (products.length) {
      const top = products[0];
      const planRow = {
        fabric: top.title.replace(/\s*[·•]\s*\d.*/, '').trim(),
        label: label,
        metres: `${metresMin}m – ${metresMax}m`,
        priceLine: top.price ? `₹${Math.round(top.price).toLocaleString('en-IN')}/metre` : ''
      };
      appendFabricPlan([planRow]);
      await delay(400);
    }

    appendBot('Tap any <em>Add to cart</em> button above to reserve the fabric. Want me to refine — different colour, lighter weave, or a tighter budget?');
  }

  // ---------------- Conversational follow-up handler ----------------
  async function handleFollowUp(text, entities) {
    showTyping(true);
    await delay(700);
    showTyping(false);

    const ruleKey = `${state.garment}:${state.occasion}`;
    const scenario = SCENARIOS[ruleKey] || SCENARIOS[`${state.garment}:casual`];
    const perPersonMin = scenario ? scenario.metres_min : 2;
    const perPersonMax = scenario ? scenario.metres_max : 2.5;

    // === Multi-person quantity scaling ===
    if (entities.persons) {
      const totalMin = (perPersonMin * entities.persons).toFixed(2);
      const totalMax = (perPersonMax * entities.persons).toFixed(2);
      const garmentLabel = state.garment || 'garment';
      appendBot(
        `For <strong>${entities.persons} persons</strong>, you'll need around <strong>${totalMin}m – ${totalMax}m total</strong> 📐<br><br>` +
        `That's <strong>${perPersonMin}m – ${perPersonMax}m per ${garmentLabel}</strong> × <strong>${entities.persons} persons</strong>.<br><br>` +
        `💡 <strong>Bulk tip:</strong> for orders above 10 metres, the studio offers wholesale pricing. WhatsApp <strong>+91 98XXX XXXXX</strong> for a custom quote, or visit us in <strong>Egmore</strong>.<br><br>` +
        `🪡 <strong>Smart shopping:</strong> mix 2-3 colours from the same fabric family — guests look coordinated without being identical.`
      );
      await delay(500);
      appendBot('Want me to suggest a 2-colour or 3-colour combination from the studio?');
      return;
    }

    // === Pricing question ===
    if (entities.intent_price) {
      appendBot(
        `Honest pricing breakdown at RIDH 💰<br><br>` +
        `<strong>Entry:</strong> ₹350–₹600/m — cotton handlooms, mul-cotton, basic linens<br>` +
        `<strong>Mid:</strong> ₹650–₹1,200/m — soft silks, chanderis, brocade-touched cottons<br>` +
        `<strong>Premium:</strong> ₹1,500–₹2,800/m — pure Kanchipuram, Banaras silks, heavy brocades<br>` +
        `<strong>Bridal:</strong> ₹3,000+ — heirloom-grade silks, hand-zari work<br><br>` +
        `🎁 First-time order ships with a <strong>free curated swatch card</strong> — six fabric snippets you can keep.`
      );
      return;
    }

    // === Care question ===
    if (entities.intent_care) {
      appendBot(
        `Care notes for each fabric family 🧼<br><br>` +
        `<strong>Cotton & linen:</strong> Machine-wash cold, mild detergent, line-dry in shade. Iron on medium.<br>` +
        `<strong>Silk:</strong> Dry-clean recommended for the first 2-3 washes; gentle hand-wash thereafter.<br>` +
        `<strong>Brocade & velvet:</strong> Dry-clean only.<br>` +
        `<strong>Pre-shrunk:</strong> Most of our cottons are pre-shrunk — they won't change size after washing.<br><br>` +
        `Each product page has the full care label.`
      );
      return;
    }

    // === Delivery question ===
    if (entities.intent_delivery) {
      appendBot(
        `Delivery timelines 📦<br><br>` +
        `<strong>Chennai metro:</strong> Same-day if ordered before 12 PM. Otherwise next day.<br>` +
        `<strong>Tamil Nadu & nearby states:</strong> 2-3 business days.<br>` +
        `<strong>Rest of India:</strong> 3-5 business days via Delhivery / BlueDart.<br>` +
        `<strong>International:</strong> 7-12 business days (DHL).<br><br>` +
        `📍 Or skip delivery — pick up directly at <strong>RIDH, Egmore, Chennai</strong>.`
      );
      return;
    }

    // === Swatch / sample question ===
    if (entities.intent_swatch) {
      appendBot(
        `Swatches & samples 🎁<br><br>` +
        `Every <strong>first order</strong> ships with a <strong>complimentary swatch card</strong> — six hand-cut fabric snippets you can touch and compare.<br><br>` +
        `Want a specific swatch before ordering? WhatsApp the product link to <strong>+91 98XXX XXXXX</strong> and we'll send one for ₹50 (refunded against your first order).<br><br>` +
        `📍 Or visit <strong>RIDH, Egmore, Chennai</strong> — every fabric on the wall is yours to touch.`
      );
      return;
    }

    // === Refinement (colour, budget, fewer metres) ===
    if (entities.colour || entities.budget_max || entities.metres) {
      appendBot('Got it — refining for you now.');
      await delay(300);
      await makeRecommendation();
      return;
    }

    // === Generic confused fall-through ===
    appendBot(
      `Hmm — I want to help. Could you tell me more specifically?<br><br>` +
      `Try things like:<br>` +
      `• <em>"How many metres for 6 people?"</em><br>` +
      `• <em>"Show me cheaper options"</em><br>` +
      `• <em>"In red instead"</em><br>` +
      `• <em>"What's the care?"</em><br>` +
      `• <em>"When will it arrive?"</em>`
    );
  }

  // ---------------- Occasion-only recommender (Festive / Casual) ----------------
  async function recommendOccasion(bucket) {
    const info = OCCASION_FABRICS[bucket];
    showTyping(true); await delay(600); showTyping(false);
    let products = [];
    const mats = state.colour ? info.materials.map(function (m) { return state.colour + ' ' + m; }).concat(info.materials) : info.materials;
    for (const m of mats) { products = await shopifySearch(m, 4); if (products.length) break; }
    if (!products.length) products = await fetchPopular(4);
    appendBot('<strong>' + info.label + ' fabrics</strong> 🧵<br><br>' + info.blurb);
    await delay(300);
    appendProducts(products, 2.5);
    state.showedProducts = true;
    await delay(300);
    appendBot('Browse the full edit: <a href="/collections/' + info.collection + '" style="color:#B8923F;text-decoration:underline;">' + info.label + ' →</a><br><br>Tell me the garment (saree, kurta, lehenga…) and I’ll size the exact metres.');
  }

  // ---------------- Dialogue logic ----------------
  // Claude backend (natural, grounded replies). Falls back to the rule engine
  // below if the backend is unreachable, returns no reply, or has no API key.
  const AI_BACKEND = (window.themeSettings && window.themeSettings.aiBackendUrl) || 'https://wati-webhook-six.vercel.app';
  async function tryBackendReply(userText) {
    if (!AI_BACKEND) return false;
    try {
      showTyping(true);
      // Send prior turns (everything except the just-added current message) so Claude
      // remembers the whole conversation. Capped to the last 8 turns to stay fast.
      const priorHistory = state.history.slice(0, -1).slice(-8);
      const res = await fetch(AI_BACKEND.replace(/\/$/, '') + '/concierge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: userText, history: priorHistory,
          sessionId: state.sessionId, phone: state.phone
        })
      });
      showTyping(false);
      if (!res.ok) return false;
      const data = await res.json();
      if (data && data.reply) {
        appendBot(data.reply);
        renderClaudeProducts(data.reply); // auto Add-to-cart cards for linked fabrics
        state.showedProducts = true;
        return true;
      }
      return false;
    } catch (e) { showTyping(false); return false; }
  }

  async function reply(userText) {
    state.turn++;

    // Try the Claude concierge first for a natural, on-brand reply.
    if (await tryBackendReply(userText)) return;

    const newEntities = parseEntities(userText);

    // === Greeting handler ===
    if (newEntities.is_greeting && !state.garment && !state.showedProducts) {
      showTyping(true); await delay(500); showTyping(false);
      const greetings = [
        "Hello! 👋 I'm RIDH — happy you're here. Tell me what you'd like to stitch — even something casual like <em>\"lehenga for cousin's wedding\"</em> or <em>\"shirt for Goa trip\"</em> works.",
        "Namaste 🙏 Tell me a bit about what you're stitching — a garment, an occasion, a season, anything — and I'll pull what suits from the studio.",
        "Hi there! Just describe what you have in mind — I understand <em>casual</em> phrasing. Examples: <em>\"saree for diwali\"</em>, <em>\"3 shirts for office\"</em>, <em>\"dress for ooty trip\"</em>."
      ];
      appendBot(greetings[Math.floor(Math.random() * greetings.length)]);
      return;
    }

    // === Acknowledgement handler ===
    if (newEntities.is_acknowledgement && state.showedProducts) {
      showTyping(true); await delay(400); showTyping(false);
      const replies = [
        "Glad I could help! Anything else I can pick for you — a matching blouse, a dupatta, accessories?",
        "You're welcome! 🙏 Tap any <em>Add to cart</em> button above to reserve, or tell me what else to suggest.",
        "Happy stitching! 🪡 If you want me to suggest a coordinating fabric (blouse, dupatta, lining), just say the word."
      ];
      appendBot(replies[Math.floor(Math.random() * replies.length)]);
      return;
    }

    // Context-reset detection: if user introduces a NEW garment OR a clearly different
    // occasion (esp. seasonal like "summer"), drop stale state so we re-evaluate.
    let wearerJustChanged = false;
    if (state.showedProducts) {
      const newGarment = newEntities.garment && newEntities.garment !== state.garment;
      const newSeason  = /\b(summer|winter|monsoon|rainy|hot|cold)\b/.test(userText.toLowerCase());
      // Wearer change = age newly provided OR child wearer flag flipped.
      // This invalidates the prior adult recommendation completely.
      const newWearer  = (newEntities.wearer_age != null && newEntities.wearer_age !== state.wearer_age)
                      || (newEntities.wearer === 'child' && state.wearer !== 'child');
      const isFollowUp = newEntities.is_question || newEntities.persons || newEntities.intent_price
                        || newEntities.intent_care || newEntities.intent_delivery || newEntities.intent_swatch;

      // Follow-up questions take priority — handle them, don't reset
      // UNLESS the user also revealed wearer info (then context shift wins).
      if (isFollowUp && !newGarment && !newSeason && !newWearer) {
        Object.assign(state, newEntities);
        await handleFollowUp(userText, newEntities);
        return;
      }
      if (newGarment || newSeason) {
        // Reset everything except potentially the new entities
        state.garment = null;
        state.occasion = null;
        state.colour = null;
        state.metres = null;
        state.budget_max = null;
        state.persons = null;
        state.location = null;
        state.season = null;
        state.showedProducts = false;
      } else if (newWearer) {
        // Keep garment + occasion, but flush the prior recommendation so it re-runs.
        state.showedProducts = false;
        wearerJustChanged = true;
      }
    }
    Object.assign(state, newEntities);

    // Acknowledge the wearer context shift before re-recommending
    if (wearerJustChanged && state.wearer === 'child') {
      const ageBit = state.wearer_age != null ? `a ${state.wearer_age}-year-old` : 'a little one';
      appendBot(`Got it — adjusting for ${ageBit}. The earlier picks were sized for an adult; here's what actually fits a child:`);
      await delay(400);
    }

    showTyping(true);
    await delay(700 + Math.random() * 600);
    showTyping(false);

    // Occasion-only fabric query (Festive / Casual) — no explicit garment, or a
    // generic "festive/casual fabric" request where garment was only mood-inferred.
    if (!state.showedProducts) {
      const wantsFabric = /\bfabrics?\b|\bmaterials?\b/i.test(userText);
      const bucket = occasionBucket(state.occasion);
      if (bucket && (!state.garment || (newEntities.mood_matched && wantsFabric))) {
        await recommendOccasion(bucket);
        return;
      }
    }

    // Path A — full info → recommend now
    if (state.garment && state.occasion) {
      await makeRecommendation();
      return;
    }

    // Path B — already shown products & user is chatting → handle refinement
    if (state.showedProducts) {
      if (state.colour || state.budget_max || state.metres) {
        appendBot('Got it — refining for you.');
        await delay(300);
        await makeRecommendation();
        return;
      }
      appendBot('Tell me what to refine — a colour you like, a tighter budget, or a different weave? Or tap any <em>Add to cart</em> button above.');
      return;
    }

    // Path C — patience defaults to break loops
    // After 3 turns: if we have garment but no occasion → assume "party" (covers birthday, casual events)
    if (state.turn >= 3 && state.garment && !state.occasion) {
      state.occasion = 'party';
      appendBot("I'll take that as a special occasion. Let me show you my best picks:");
      await delay(400);
      await makeRecommendation();
      return;
    }
    // After 4 turns: still nothing → guess saree + festival (most common combo)
    if (state.turn >= 4) {
      state.garment = state.garment || 'saree';
      state.occasion = state.occasion || 'festival';
      appendBot("Let me share what's loved most at the studio right now — perfect for any special moment:");
      await delay(400);
      await makeRecommendation();
      return;
    }

    // Path D — still gathering info
    if (!state.garment) {
      if (state.turn === 1) {
        appendBot("Lovely! What would you like to stitch? A <strong>saree</strong>, <strong>kurta</strong>, <strong>lehenga</strong>, <strong>shirt</strong>, <strong>sherwani</strong>, <strong>blouse</strong>, or <strong>dupatta</strong>? <em>Typos are fine — I understand.</em>");
      } else if (state.turn === 2) {
        appendBot("No worries — I didn't catch the garment. Just type one word: <em>saree</em>, <em>kurta</em>, <em>lehenga</em>, <em>shirt</em>, <em>sherwani</em>, <em>blouse</em>, <em>dupatta</em>, or <em>dress</em>.");
      } else {
        // After 2 attempts to get garment, just commit to saree (most popular at fabric studios)
        appendBot("Let me just show you what's trending at the studio — sarees in our most-loved silks. Tell me to refine if you want something else.");
        await delay(400);
        state.garment = 'saree';
        state.occasion = state.occasion || 'festival';
        await makeRecommendation();
      }
      return;
    }
    if (!state.occasion) {
      // If we got the garment through fuzzy match, acknowledge it first
      const fuzzyAck = newEntities.was_fuzzy ? `Got it — <strong>${state.garment}</strong>! ` : '';
      if (state.turn <= 2) {
        appendBot(`${fuzzyAck}A <strong>${escapeHtml(state.garment)}</strong> — beautiful choice. What's the occasion? <strong>Wedding</strong>, <strong>festival</strong>, <strong>party / birthday</strong>, <strong>office</strong>, or <strong>daily wear</strong>?`);
      } else {
        // Don't keep asking — default to most common occasion for that garment
        const defaultOccasion = state.garment === 'sherwani' || state.garment === 'lehenga' ? 'wedding'
                              : state.garment === 'shirt' ? 'casual'
                              : 'festival';
        appendBot(`Let me suggest for a <strong>${defaultOccasion}</strong> ${escapeHtml(state.garment)} — most popular request. Tell me to refine if you had something else in mind.`);
        await delay(400);
        state.occasion = defaultOccasion;
        await makeRecommendation();
      }
      return;
    }
  }

  function delay(ms) { return new Promise(r => setTimeout(r, ms)); }

  // ---------------- Initial greeting ----------------
  /* ---------------- Phone gate ----------------
     The chat asks for a mobile number before it starts. Nothing is answered until a
     valid Indian mobile is given — that number is what ties every message of this
     customer together (and to their Customer ID) in the AI Chat Log sheet, so the
     studio can follow up on a conversation. Remembered on this device so a returning
     customer is not asked again. */
  const PHONE_KEY = 'ridh_chat_phone';

  // Accepts 9876543210, +91 98765 43210, 09876543210 … → returns "9876543210" or null.
  function normalisePhone(raw) {
    let d = String(raw || '').replace(/\D/g, '');
    if (d.length === 12 && d.slice(0, 2) === '91') d = d.slice(2);
    else if (d.length === 13 && d.slice(0, 3) === '091') d = d.slice(3);
    else if (d.length === 11 && d[0] === '0') d = d.slice(1);
    return /^[6-9]\d{9}$/.test(d) ? d : null;
  }

  function loadPhone() {
    try { return normalisePhone(localStorage.getItem(PHONE_KEY)); } catch (e) { return null; }
  }
  function savePhone(p) {
    try { localStorage.setItem(PHONE_KEY, p); } catch (e) { /* private mode — fine */ }
  }

  const ASK_PHONE = "Before we begin, may I have your <strong>10-digit mobile number</strong>? " +
    "It lets our team follow up with fabric photos, prices and availability — and we'll never spam you. 📱";

  async function welcome() {
    appendBot("Namaste! I'm RIDH — your fabric concierge. Tell me what you have in mind: <em>garment, occasion, colour, size</em> — and I'll find the right fabrics from the studio and add them to your cart.");
    await delay(400);
    appendBot("Try: <em>\"Saree for a wedding, ivory or gold, around ₹2500/metre\"</em>");
  }

  async function greet() {
    await delay(300);
    showTyping(true);
    await delay(900);
    showTyping(false);
    state.phone = loadPhone();
    if (state.phone) { await welcome(); return; }   // already known on this device
    appendBot(ASK_PHONE);
  }

  // ---------------- Hook up form ----------------
  form && form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = (input.value || '').trim();
    if (!text) return;
    appendUser(text);
    input.value = '';

    // No number yet → this message must BE the number. The chat does not proceed
    // until it is a valid one.
    if (!state.phone) {
      const p = normalisePhone(text);
      if (!p) {
        await delay(250);
        appendBot("That doesn't look like a valid Indian mobile number. Please enter your " +
                  "<strong>10-digit number</strong> — for example <em>98765 43210</em>. 🙏");
        return;
      }
      state.phone = p;
      savePhone(p);
      await delay(250);
      appendBot("Thank you! ✅ Saved <strong>" + escapeHtml(p) + "</strong> — our team can reach you on this number.");
      await delay(300);
      await welcome();
      return;
    }

    reply(text);
  });

  // Start
  greet();
})();
